// src/components/ViewWork.jsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Popup from "reactjs-popup";
import { FaTimesCircle } from "react-icons/fa";
import { fetchMeterBrands, getImageProxyUrl, fetchQnoms, fetchDiameters } from "../../data/fetchData";
import { formatDecimal } from "../../utils/formatters";

const TYPE_LABELS = {
  MAIN_GAUGE: "Põhiarvesti",
  SUB_GAUGE: "Alamarvesti",
  WATERING: "Kastmine",
};

export default function ViewWork() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const work = state.work;



  const [brandOptions, setBrandOptions] = useState([]);
  const [qnomOptions, setQnomOptions] = useState([]);
  const [diameterOptions, setDiameterOptions] = useState([]);

  // load brands list so we can map ID → name
  useEffect(() => {
    fetchMeterBrands().then(setBrandOptions).catch(console.error);
    // also load q_nom and diameter options for label mapping
    fetchQnoms().then(setQnomOptions).catch(console.error);
    fetchDiameters().then(setDiameterOptions).catch(console.error);
  }, []);

  const actionLabel = work.action === "add" ? "Lisamine" : "Eemaldamine";
  const typeLabel = TYPE_LABELS[work.type] || work.type;

  // lookup our brand name
  const brandName = (() => {
    const b = brandOptions.find((b) => b.id === work.meter_brand);
    return b ? b.name : work.meter_brand;
  })();

  // resolve Qn and diameter labels – fall back to raw value / id if look-ups not yet loaded
  const qNomLabel = (() => {
    // direct value from backend if available
    if (work.q_nom_value) return formatDecimal(work.q_nom_value);
    // try lookup by ID
    const q = qnomOptions.find((o) => o.id === work.q_nom);
    return q ? formatDecimal(q.q_nom) : work.q_nom;
  })();

  const diamLabel = (() => {
    if (work.diameter_value) return formatDecimal(work.diameter_value);
    const d = diameterOptions.find((o) => o.id === work.diameter);
    return d ? formatDecimal(d.diameter) : work.diameter;
  })();

  const onConsole = work.on_console ? "Jah" : "Ei";

  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString("et-EE", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "—";

  return (
    <div className="max-w-4xl mx-auto mt-24 p-6 bg-white shadow-lg rounded-lg border border-gray-300">
      <div className="bg-primary text-white text-center py-4 rounded-t-lg">
        <h1 className="text-xl font-bold">Töö detailid</h1>
      </div>

      <table className="w-full table-auto">
        <tbody>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Arvesti number</th>
            <td className="p-2 border-t border-gray-200">{work.meter_number || "—"}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Kuupäev</th>
            <td className="p-2 border-t border-gray-200">
              {fmtDate(work.date)}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Aadress</th>
            <td className="p-2 border-t border-gray-200">
              {work.estate_address}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Omanik</th>
            <td className="p-2 border-t border-gray-200">{work.client_name}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Töö liik</th>
            <td className="p-2 border-t border-gray-200">{actionLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Tüüp</th>
            <td className="p-2 border-t border-gray-200">{typeLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Veemõõtja mark
            </th>
            <td className="p-2 border-t border-gray-200">{brandName}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Qn/m³</th>
            <td className="p-2 border-t border-gray-200">{qNomLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Läbimõõt</th>
            <td className="p-2 border-t border-gray-200">{diamLabel}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Arvesti näit
            </th>
            <td className="p-2 border-t border-gray-200">{formatDecimal(work.reading)}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Viimane taatlemine
            </th>
            <td className="p-2 border-t border-gray-200">
              {fmtDate(work.last_certification_date)}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Plommimise kuupäev
            </th>
            <td className="p-2 border-t border-gray-200">
              {fmtDate(work.verification_date)}
            </td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Plommi number
            </th>
            <td className="p-2 border-t border-gray-200">{work.seal_number}</td>
          </tr>
          <tr>
            <th className="p-2 text-left border-t border-gray-200">
              Konsoolil
            </th>
            <td className="p-2 border-t border-gray-200">{onConsole}</td>
          </tr>
          {work.notes && (
            <tr>
              <th className="p-2 text-left border-t border-gray-200">
                Märkused
              </th>
              <td className="p-2 border-t border-gray-200">{work.notes}</td>
            </tr>
          )}
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Teostas</th>
            <td className="p-2 border-t border-gray-200">
              {work.performed_by_name ?? work.performed_by}
            </td>
          </tr>

          {/* signature */}
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Allkiri</th>
            <td className="p-2 border-t border-gray-200">
              {work.signature ? (
                <Popup
                  modal
                  trigger={
                    <img
                      src={work.signature}
                      alt="Signature"
                      className="w-24 h-auto object-cover rounded-md cursor-pointer"
                    />
                  }
                  closeOnDocumentClick={false}
                >
                  {(close) => (
                    <div className="relative p-4">
                      <img
                        src={work.signature}
                        alt="Signature"
                        className="w-full h-auto max-w-3xl mx-auto"
                      />
                      <button
                        onClick={close}
                        className="absolute top-2 right-2 text-2xl"
                      >
                        <FaTimesCircle />
                      </button>
                    </div>
                  )}
                </Popup>
              ) : (
                <em>Pole allkirja</em>
              )}
            </td>
          </tr>

          {/* images */}
          <tr>
            <th className="p-2 text-left border-t border-gray-200">Pildid</th>
            <td className="p-2 border-t border-gray-200">
              <div className="flex space-x-4">
                {work.images?.map((img, idx) => {
                  // Use backend proxy URL for images
                  const imageUrl = getImageProxyUrl(img.id);

                  return (
                    <Popup
                      key={idx}
                      modal
                      trigger={
                        <img
                          src={imageUrl}
                          alt={`Work ${idx + 1}`}
                          className="w-24 h-24 object-cover rounded-md cursor-pointer"
                        />
                      }
                      closeOnDocumentClick={false}
                    >
                      {(close) => (
                        <div className="relative p-4">
                          <img
                            src={imageUrl}
                            alt={`Work ${idx + 1}`}
                            className="w-full h-auto max-w-3xl mx-auto"
                          />
                          <button
                            onClick={close}
                            className="absolute top-2 right-2 text-2xl"
                          >
                            <FaTimesCircle />
                          </button>
                        </div>
                      )}
                    </Popup>
                  );
                })}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="flex justify-end mt-4">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
        >
          Tagasi
        </button>
      </div>
    </div>
  );
}
